/**
 * GLOSSARY PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Dark tool mode.
 */

import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon, BookOpenIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Screen, Section, Panel, Button, Badge, Input, Select } from '../design/primitives';
import { SPACING, COLORS, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { GlossaryEntry } from '../types/glossary';
import GlossaryForm from '../components/forms/GlossaryForm';

const GlossaryPage: React.FC = () => {
  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<GlossaryEntry | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [tagFilter, setTagFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchData();
    fetchAllTags();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/api/glossary/');
      setEntries(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load glossary entries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTags = async () => {
    try {
      const response = await apiClient.get('/api/glossary/all_tags/');
      setAllTags(response.data);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await apiClient.delete(`/api/glossary/${id}/`);
      await fetchData();
    } catch (err) {
      setError('Failed to delete entry');
    }
  };

  const handleToggleLearned = async (entry: GlossaryEntry) => {
    try {
      await apiClient.patch(`/api/glossary/${entry.id}/`, {
        is_learned: !entry.is_learned
      });
      await fetchData();
    } catch (err) {
      setError('Failed to update entry');
    }
  };

  const handleEdit = (entry: GlossaryEntry) => {
    setEditingEntry(entry);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingEntry(null);
  };

  const handleFormSuccess = () => {
    fetchData();
    fetchAllTags();
    handleFormClose();
  };

  const filteredEntries = entries.filter(entry => {
    if (searchTerm && !entry.word.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (tagFilter !== 'all') {
      if (!entry.tags || !entry.tags.includes(tagFilter)) return false;
    }
    if (statusFilter === 'learned' && !entry.is_learned) return false;
    if (statusFilter === 'learning' && entry.is_learned) return false;
    return true;
  });

  const totalEntries = entries.length;
  const learnedCount = entries.filter(e => e.is_learned).length;
  const learningCount = totalEntries - learnedCount;

  if (isLoading) {
    return (
      <Screen>
        <Section title="Personal Glossary">
          <div style={{ textAlign: 'center', padding: SPACING[48], color: COLORS.text.tertiary }}>
            Loading...
          </div>
        </Section>
      </Screen>
    );
  }

  return (
    <Screen>
      <Section
        title="Personal Glossary"
        action={
          <Button
            variant="primary"
            onClick={() => setIsFormOpen(true)}
            icon={<PlusIcon style={{ width: '16px', height: '16px' }} />}
          >
            Add Word
          </Button>
        }
      >
        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: SPACING[16],
          marginBottom: SPACING[24],
        }}>
          <Panel>
            <div style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.small, marginBottom: SPACING[4] }}>
              Total Words
            </div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.headings.weight, color: COLORS.text.primary }}>
              {totalEntries}
            </div>
          </Panel>
          <Panel>
            <div style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.small, marginBottom: SPACING[4] }}>
              Learned
            </div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.headings.weight, color: COLORS.status.success }}>
              {learnedCount}
            </div>
          </Panel>
          <Panel>
            <div style={{ color: COLORS.text.secondary, fontSize: TYPOGRAPHY.body.sizes.small, marginBottom: SPACING[4] }}>
              Learning
            </div>
            <div style={{ fontSize: TYPOGRAPHY.headings.sizes.h2, fontWeight: TYPOGRAPHY.headings.weight, color: COLORS.accent.default }}>
              {learningCount}
            </div>
          </Panel>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: SPACING[24] }}>
        <Panel>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: SPACING[16],
          }}>
            <Input
              placeholder="Search words..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<MagnifyingGlassIcon style={{ width: '16px', height: '16px' }} />}
            />
            <Select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Tags' },
                ...allTags.map(tag => ({ value: tag, label: tag })),
              ]}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'learning', label: 'Learning' },
                { value: 'learned', label: 'Learned' },
              ]}
            />
          </div>
        </Panel>
        </div>

        {error && (
          <div style={{
            padding: SPACING[12],
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${COLORS.status.error}`,
            borderRadius: RADIUS,
            color: COLORS.status.error,
            marginBottom: SPACING[16],
          }}>
            {error}
          </div>
        )}

        {/* Entries List */}
        {filteredEntries.length === 0 ? (
          <Panel>
            <div style={{ textAlign: 'center', padding: SPACING[48] }}>
              <BookOpenIcon style={{ width: '48px', height: '48px', color: COLORS.text.tertiary, margin: '0 auto', marginBottom: SPACING[16] }} />
              <div style={{ color: COLORS.text.tertiary }}>
                {searchTerm || tagFilter !== 'all' || statusFilter !== 'all'
                  ? 'No entries match your filters'
                  : 'No glossary entries yet. Add your first word!'}
              </div>
            </div>
          </Panel>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
            {filteredEntries.map((entry) => (
              <Panel key={entry.id} style={{
                borderLeft: entry.is_learned ? `4px solid ${COLORS.status.success}` : `4px solid ${COLORS.border.default}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12], marginBottom: SPACING[8] }}>
                      <h3 style={{
                        fontFamily: TYPOGRAPHY.headings.fontFamily,
                        fontSize: TYPOGRAPHY.headings.sizes.h3,
                        fontWeight: TYPOGRAPHY.headings.weight,
                        color: COLORS.text.primary,
                        margin: 0,
                      }}>
                        {entry.word}
                      </h3>
                      {entry.is_learned && <Badge color="success">Learned</Badge>}
                    </div>

                    {entry.definition && (
                      <p style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: COLORS.text.primary,
                        marginBottom: SPACING[8],
                      }}>
                        <strong>Definition:</strong> {entry.definition}
                      </p>
                    )}

                    {entry.example && (
                      <p style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: COLORS.text.secondary,
                        fontStyle: 'italic',
                        marginBottom: SPACING[8],
                      }}>
                        <strong style={{ fontStyle: 'normal' }}>Example:</strong> {entry.example}
                      </p>
                    )}

                    {entry.source && (
                      <p style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.text.tertiary,
                        marginBottom: SPACING[8],
                      }}>
                        <strong>Source:</strong> {entry.source}
                      </p>
                    )}

                    {entry.tags && entry.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: SPACING[8], flexWrap: 'wrap', marginTop: SPACING[8] }}>
                        {entry.tags.map((tag) => (
                          <Badge key={tag} color="accent">#{tag}</Badge>
                        ))}
                      </div>
                    )}

                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: COLORS.text.tertiary,
                      marginTop: SPACING[8],
                    }}>
                      Added {new Date(entry.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: SPACING[8], marginLeft: SPACING[16] }}>
                    <Button
                      variant={entry.is_learned ? 'secondary' : 'primary'}
                      onClick={() => handleToggleLearned(entry)}
                    >
                      {entry.is_learned ? 'Mark Learning' : 'Mark Learned'}
                    </Button>
                    <button
                      onClick={() => handleEdit(entry)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.text.secondary,
                        cursor: 'pointer',
                        padding: SPACING[8],
                      }}
                    >
                      <PencilIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.status.error,
                        cursor: 'pointer',
                        padding: SPACING[8],
                      }}
                    >
                      <TrashIcon style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </Section>

      <GlossaryForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        initialEntry={editingEntry}
        isEditMode={!!editingEntry}
      />
    </Screen>
  );
};

export default GlossaryPage;
